import { useMemo, useCallback } from "react"
import { useSelector } from "react-redux"
import { map, chain, reduce, capitalize, compact, camelCase, isFunction, keyBy, get, isEmpty } from "lodash"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { getTradePosition } from "connected-components/dashboard/helpers/trades"
import { RULES } from "connected-components/compare/position-sets"
import { CONTENT_SET_DATA_TYPES } from "helpers/contentSetConstants"
import { useModelBetaFields } from "resources/beta"
import { useContentSetsMetadata } from "resources/content-sets"
import { useModelMetadata } from "resources/models"
import { useResourcesList } from "resources/resource"
import { useClassificationsData } from "resources/classifications"
import { useAttributeCategories } from "resources/attributes"
import { useDatasets } from "resources/datasets"
import { useIsOpPro, useIsRfrAttributionEnabled } from "resources/account"
import { BETA_FACTORS_WITH_DYNAMIC } from "helpers/analyzeConstants"
import { combineTanStackStatuses, OK } from "helpers/data-statuses"
import { METRIC_OPTIONS } from "./definitions"
import { VALUE_FORMATS, INTRADAY_METRIC_CATEGORY } from "./constants"
import { ASSET_METRIC_CATEGORIES, getMarginalContributionMetrics, ASSET_METRICS_BY_ID } from "./assets"
import { FACTOR_METRIC_CATEGORIES, FACTOR_METRICS_BY_ID } from "./factors"
import AttributeTooltip from "./attribute-tooltip"
import { makeDataPath, isInstrumentWithoutGMV, filterColumnsBasedOnGMV, createMetricGroup, createMetricTypeOptions, hasAction, makeMetricLabel, getDefaultDownloadMetrics, getDataGetter, makeTooltipText, makeTooltipContents, makeDownloadLabel, getFilteredTriplicatedColumns, mapDescriptorItems } from "./utils"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"

export function useAssetViewDashboardMetrics(metricsAndCategories, originalInstrument, rebalancedInstrument, comparisonInstrument, options = {}) {
  const { instrumentColumns, setTradedPosition, instrumentsWithMetadata, isExpanded, disableActiveTrading, isEndVsStartDateComparison } = options
  const handleSetTradePosition = useCallback((position, instrumentType, path) => {
    const [positionToTrade, tradeInstrumentType] = getTradePosition(position, instrumentType, path)
    setTradedPosition(positionToTrade, [tradeInstrumentType, path])
  }, [setTradedPosition])
  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()

  const isCompareWeightStrategy = useMemo(
    () => instrumentsWithMetadata?.comparison?.type === RULES,
    [instrumentsWithMetadata],
  )

  const selectedMetrics = useMemo(() => {
    const defaultAssetViewMetrics = getDefaultAssetViewMetrics({ handleSetTradePosition, instrumentColumns, originalInstrument, rebalancedInstrument, comparisonInstrument, isExpanded, disableActiveTrading })
    return computeMetricsFromDefinition(
      [...defaultAssetViewMetrics, ...metricsAndCategories],
      { ...options, isRfrAttributionEnabled },
    )
  }, [metricsAndCategories, options, handleSetTradePosition, instrumentColumns, originalInstrument, rebalancedInstrument, comparisonInstrument, isExpanded, disableActiveTrading, isRfrAttributionEnabled])

  return useMemo(() =>
    processAssetViewMetrics(selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isCompareWeightStrategy),
  [selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isCompareWeightStrategy])
}

export function useFactorViewDashboardMetrics(metricsAndCategories, options = {}) {
  const { instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison } = options
  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()
  const selectedMetrics = useMemo(() => {
    return computeMetricsFromDefinition(
      [{ id: "factorLabel" }, ...metricsAndCategories],
      { ...options, isRfrAttributionEnabled },
    )
  }, [metricsAndCategories, options, isRfrAttributionEnabled])

  const isWeightStrategy = useMemo(
    () => instrumentsWithMetadata?.comparison?.type === RULES,
    [instrumentsWithMetadata],
  )

  return useMemo(() =>
    processFactorViewMetrics(selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isWeightStrategy),
  [selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isWeightStrategy])
}

function useClassificationMetrics(date) {
  const classificationsData = useClassificationsData(date)
  const { classifications, classificationsValues, status } = classificationsData || {}

  const classificationsMetrics = useMemo(() => {
    if (!classifications || !classificationsValues) {
      return []
    }
    return chain(classifications)
      .map(({ id: classificationId, name: classificationName }) => {
        const classification = classificationsValues[classificationId]
        if (!classification) {
          return null
        }
        const tiers = classification.version?.tiers
        return {
          id: classificationId,
          label: classificationName,
          items: tiers ? tiers.map(tier => ({
            id: tier.id,
            dataPath: `descriptors.${classificationId.toLowerCase()}_${tier.id.toLowerCase()}`,
            prefix: "descriptors",
            label: tier.name,
            format: VALUE_FORMATS.STRING,
            category: classificationName,
            singleFactor: true,
          })) : [],
        }
      })
      .compact()
      .value()
  }, [classificationsValues, classifications])
  return [classificationsMetrics, status]
}

function useContentSetMetrics() {
  const [contentSets, contentSetsStatus] = useContentSetsMetadata()

  const contentSetMetrics = useMemo(() => map(contentSets, (contentSet) => {
    const metricOptions = contentSet.dataType === CONTENT_SET_DATA_TYPES.SENSITIVITY ? METRIC_OPTIONS.sensitivityContentSet : METRIC_OPTIONS.contentSet
    const filteredMetricOptions = metricOptions.filter(option => !option.disabled)
    return createMetricGroup(contentSet.id, contentSet, {
      metricOptions: filteredMetricOptions,
      name: contentSet.name,
    })
  }), [contentSets])
  return [contentSetMetrics, contentSetsStatus]
}

function useMarginalContributionMetrics(modelMetadata) {
  const isOpPro = useIsOpPro()
  const metrics = useMemo(() => {
    if (isOpPro) {
      return null
    }
    const marginalContributionMetrics = getMarginalContributionMetrics(modelMetadata)
    const categories = chain(marginalContributionMetrics)
      .groupBy("category")
      .map((metrics, category) => ({
        id: camelCase(category),
        label: category,
        items: metrics.map((metric) => ({
          ...metric,
          category,
        })),
      }))
      .value()

    return {
      id: "marginal",
      label: "Marginal Contribution",
      categories,
      items: categories.map((category) => ({
        ...category,
        items: category.items.map((item) => ({
          ...item,
          metricTypeOptions: createMetricTypeOptions([METRIC_OPTIONS.default], { prefix: "marginal", dataPath: item.dataPath, format: item.format }),
        })),
      })),
    }
  }, [modelMetadata, isOpPro])
  return [metrics, OK]
}

function useBetaMetrics(modelId, betaInstrument) {
  const [modelBetaFields, modelBetaFieldsStatus] = useModelBetaFields(modelId)
  const betaFields = useMemo(() => betaInstrument ? [...modelBetaFields, "dynamicPredicted"] : modelBetaFields, [betaInstrument, modelBetaFields])
  const betaMetrics = useMemo(() => ({
    id: "beta",
    label: "Beta",
    items: map(betaFields, (betaId) => ({
      id: betaId,
      label: BETA_FACTORS_WITH_DYNAMIC[betaId].label,
      category: "beta",
      metricTypeOptions: createMetricTypeOptions(METRIC_OPTIONS.beta, { prefix: "beta", factorId: betaId }),
    })),
  }), [betaFields])
  return [betaMetrics, modelBetaFieldsStatus]
}

function useRiskModelMetrics(modelMetadata) {
  const isOpPro = useIsOpPro()

  return useMemo(() => {
    if (!modelMetadata) {
      return {}
    }

    const metricsWithOptions = compact([
      isOpPro ? null : { id: "risk", name: "Risk", metricOptions: [{ ...METRIC_OPTIONS.default, format: VALUE_FORMATS.PERCENT }], canAggregateCategories: true },
      { id: "exposure", name: "Exposure", metricOptions: METRIC_OPTIONS.exposure },
      isOpPro ? null : { id: "predictedFactorSensitivity", name: "Predicted Factor Sensitivity", metricOptions: METRIC_OPTIONS.factorSensitivity },
      { id: "historicalFactorSensitivity", name: "Historical Factor Sensitivity", metricOptions: METRIC_OPTIONS.factorSensitivity },
      { id: "performance", name: "Performance", metricOptions: METRIC_OPTIONS.performance, canAggregateCategories: true },
    ])

    return metricsWithOptions.reduce((acc, { id, name, metricOptions, canAggregateCategories }) => {
      acc[id] = createMetricGroup(id, modelMetadata, { name, metricOptions, canAggregateCategories, modelId: modelMetadata.id })
      return acc
    }, {})
  }, [modelMetadata, isOpPro])
}

function useDescriptorMetrics() {
  const [datasets, datasetsStatus] = useDatasets()
  return useMemo(() => {
    let entitledDescriptors = []
    if (datasets && datasetsStatus === OK) {
      const entitledDatasets = keyBy(datasets, "id")
      entitledDescriptors = ["sedol", "cusip", "isin"].filter(dataset => entitledDatasets[dataset.toUpperCase()])
    }
    const stringDescriptors = ["figi", "bloombergTicker", "assetClass", "assetSubclass", "ticker", "mic", "description", "country", "currency", ...entitledDescriptors]
    const currencyDescriptors = ["averageDailyVolume", "marketCapitalization"]
    return [{
      id: "descriptors",
      label: "Descriptors",
      items: [
        ...mapDescriptorItems(stringDescriptors, VALUE_FORMATS.STRING),
        ...mapDescriptorItems(currencyDescriptors, VALUE_FORMATS.CURRENCY),
      ],
    }, OK]
  }, [datasets, datasetsStatus])
}

function usePerformanceSummaryMetrics() {
  return useMemo(() => ({
    id: "summary",
    label: "Summary",
    items: [
      ASSET_METRICS_BY_ID.totalReturn({ category: "Performance Contribution" }),
      ASSET_METRICS_BY_ID.specificReturn({ category: "Performance Contribution" }),
      ASSET_METRICS_BY_ID.factorsReturn({ category: "Performance Contribution" }),
      ASSET_METRICS_BY_ID.tradingReturn({ category: "Performance Contribution" }),
      ASSET_METRICS_BY_ID.residualReturn({ category: "Performance Contribution" }),
    ].map(metric => ({
      ...metric,
      metricTypeOptions: createMetricTypeOptions(METRIC_OPTIONS.performance, { prefix: "performance", factorId: metric.id }),
    })),
  }), [])
}

function useRiskSummaryMetrics() {
  return useMemo(() => ({
    id: "summary",
    label: "Summary",
    items: [
      ASSET_METRICS_BY_ID.riskTotalContribution(),
      ASSET_METRICS_BY_ID.riskSpecificContribution(),
      ASSET_METRICS_BY_ID.riskFactorsContribution(),
    ].map(metric => ({
      ...metric,
      metricTypeOptions: [{ ...METRIC_OPTIONS.default, dataPath: metric.dataPath, format: VALUE_FORMATS.PERCENT, prefix: "risk" }],
    })),
  }), [])
}

function useForecastMetrics() {
  const isOpPro = useIsOpPro()
  const [forecasts, forecastsStatus] = useResourcesList("forecast")
  const forecastMetrics = useMemo(() => {
    const impliedReturns = {
      id: "impliedReturns",
      name: "Implied Returns",
    }

    const currentForecasts = forecasts || []
    const metricsWithImpliedReturns = isOpPro ? currentForecasts : [...currentForecasts, impliedReturns]
    const forecastsWithMetrics = metricsWithImpliedReturns.map((forecast) => ({
      id: forecast.id,
      label: forecast.name,
      items: [
        ASSET_METRICS_BY_ID.securityExpectedReturn({ forecastId: forecast.id }),
        ASSET_METRICS_BY_ID.weightedExpectedReturn({ forecastId: forecast.id }),
      ],
    }))

    return {
      label: "Forecasts",
      items: forecastsWithMetrics.map((forecast) => ({
        ...forecast,
        items: forecast.items.map((metric) => ({
          ...metric,
          category: forecast.name,
          metricTypeOptions: createMetricTypeOptions([METRIC_OPTIONS.default], { prefix: forecast.id, factorId: metric.id }),
        })),
      })),
    }
  }, [forecasts, isOpPro])
  return [forecastMetrics, forecastsStatus]
}

function enhanceAttributeValue(label, currency, item, formattedValue, metric) {
  if (formattedValue === "-") {
    return formattedValue
  }

  const attributeValues = get(item.values, metric.dataPath)
  const items = attributeValues?.items ?? []
  const exposures = metric.isCompareWeightStrategy ? [] : (attributeValues?.exposures ?? [])

  return (
    <AttributeTooltip
      label={label}
      currency={currency}
      items={items}
      exposures={exposures}
    />
  )
}

function makeAttributeCategory(itemCategories, prefix, currencyOverride) {
  const label = capitalize(prefix)
  const singularLabel = label.slice(0, -1)
  return {
    label,
    id: prefix,
    items: itemCategories.map(({ id, name }) => ({
      id,
      label: name,
      category: label,
      format: VALUE_FORMATS.STRING,
      dataPath: `${prefix}.${id}`,
      singleFactor: true,
      prefix,
      downloadDataGetter: (values, options) => {
        const { exposures, items } = get(values, options.dataPath) ?? {}
        if (isEmpty(items) || isEmpty(exposures)) {
          return ""
        }
        if (items.length === 1) {
          return items[0]?.toString()
        }
        return items
          .map((item, index) => `${item} (${exposures[index]})`)
          .join("\n")
      },
      enhanceValue: (...props) => enhanceAttributeValue(singularLabel, currencyOverride, ...props),
    })),
  }
}

function useAttributeCategoryMetrics(instrument) {
  const [attributeCategories, attributeCategoriesStatus] = useAttributeCategories(instrument)
  const currencyOverride = instrument?.currencyOverride
  return useMemo(() => {
    if (attributeCategoriesStatus !== OK || !attributeCategories?.length) {
      return [[], attributeCategoriesStatus]
    }
    return [
      makeAttributeCategory(attributeCategories, "attributes", currencyOverride),
      attributeCategoriesStatus,
    ]
  }, [attributeCategories, attributeCategoriesStatus, currencyOverride])
}

export function useMetricSelectorMetrics(modelId, instrument, betaInstrument, date) {
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)
  const isPortfolio = instrument?.type === "PORTFOLIO"
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)

  const [classificationsMetrics, classificationsStatus] = useClassificationMetrics(date)
  const [contentSetsMetrics, contentSetsMetricsStatus] = useContentSetMetrics()
  const [betaMetrics, betaMetricsStatus] = useBetaMetrics(modelId, betaInstrument)
  const [attributeCategoryMetrics, attributeCategoryMetricsStatus] = useAttributeCategoryMetrics(isPortfolio ? instrument : null)
  const [marginalContributionMetrics, marginalContributionMetricsStatus] = useMarginalContributionMetrics(modelMetadata)

  const riskModelMetrics = useRiskModelMetrics(modelMetadata)
  const { risk, exposure, predictedFactorSensitivity, historicalFactorSensitivity, performance } = riskModelMetrics

  const [descriptorMetrics, descriptorMetricsStatus] = useDescriptorMetrics()
  const performanceSummaryMetrics = usePerformanceSummaryMetrics()
  const riskSummaryMetrics = useRiskSummaryMetrics()
  const [forecastMetrics, forecastMetricsStatus] = useForecastMetrics()
  const performanceMetrics = useMemo(() => {
    if (!performance) {
      return null
    }

    const performanceItems = performance.items || []
    return {
      ...performance,
      items: [...performanceItems, performanceSummaryMetrics],
    }
  }, [performance, performanceSummaryMetrics])

  const status = useMemo(() =>
    combineTanStackStatuses([modelMetadataStatus, contentSetsMetricsStatus, forecastMetricsStatus, betaMetricsStatus, classificationsStatus, descriptorMetricsStatus, attributeCategoryMetricsStatus, marginalContributionMetricsStatus]),
  [modelMetadataStatus, contentSetsMetricsStatus, forecastMetricsStatus, betaMetricsStatus, classificationsStatus, descriptorMetricsStatus, attributeCategoryMetricsStatus, marginalContributionMetricsStatus])

  const intraday = isIntradayAnalyticsEnabled ? INTRADAY_METRIC_CATEGORY : undefined
  const metrics = useMemo(() => chain([
    descriptorMetrics,
    risk ? { ...risk, items: [...(risk.items || []), riskSummaryMetrics] } : null,
    marginalContributionMetrics,
    exposure,
    betaMetrics,
    predictedFactorSensitivity,
    historicalFactorSensitivity,
    forecastMetrics,
    performanceMetrics,
    attributeCategoryMetrics,
    ...classificationsMetrics,
    ...contentSetsMetrics,
    intraday,
  ])
    .filter(metricCategory => metricCategory && metricCategory.items?.length > 0)
    .value(),
  [descriptorMetrics, risk, exposure, betaMetrics, predictedFactorSensitivity, historicalFactorSensitivity, performanceMetrics, marginalContributionMetrics, contentSetsMetrics, riskSummaryMetrics, forecastMetrics, classificationsMetrics, attributeCategoryMetrics, intraday])

  return [metrics, status]
}

function getDefaultAssetViewMetrics({ handleSetTradePosition, instrumentColumns, originalInstrument, rebalancedInstrument, comparisonInstrument, isExpanded, disableActiveTrading }) {
  return [
    {
      id: "marketValue",
      shouldHide: () => instrumentColumns.some(elem => ["original", "rebalanced"].includes(elem)) && isInstrumentWithoutGMV(originalInstrument.type),
      filterInstrumentColumns: (instrumentColumns) => filterColumnsBasedOnGMV(instrumentColumns, originalInstrument, rebalancedInstrument, comparisonInstrument),
      actionHandler: !isExpanded ? handleSetTradePosition : null,
    },
    {
      id: "percentEquity",
      actionHandler: !isExpanded && !disableActiveTrading ? handleSetTradePosition : null,
    },
  ]
}

function transformMetric(metric, instrumentType, index, instrumentColumns, instrumentsWithMetadata, filteredInstrumentColumns, isEndVsStartDateComparison, isCompareWeightStrategy) {
  const suffix = INSTRUMENTS[instrumentType].metricsSuffix
  const isOptimization = instrumentsWithMetadata?.[instrumentType]?.isOptimization ?? false
  return {
    ...metric,
    // Don't include enhanceValue for delta columns since they compute differences and don't have residual values
    ...(instrumentType === "delta" && { enhanceValue: undefined }),
    id: `${metric.id}${capitalize(INSTRUMENTS[instrumentType].suffix)}`,
    prefix: metric.id,
    label: makeMetricLabel(metric, instrumentType, instrumentColumns, isEndVsStartDateComparison, isCompareWeightStrategy, isOptimization),
    isCompareWeightStrategy: instrumentType === "comparison" && isCompareWeightStrategy,
    dataPath: makeDataPath(metric.dataPath, suffix),
    baseDataPath: metric.dataPath,
    instrumentSuffix: suffix,
    dataGetter: getDataGetter(metric, instrumentType, instrumentColumns),
    tooltipText: makeTooltipText(instrumentType, metric, instrumentsWithMetadata, isCompareWeightStrategy),
    isFirstInstrument: filteredInstrumentColumns?.length > 1 && index === 0,
    tooltipContents: makeTooltipContents(metric, { instrumentType }),
    downloadLabel: makeDownloadLabel(instrumentsWithMetadata, instrumentType, metric, isEndVsStartDateComparison, isCompareWeightStrategy),
    format: metric.format || VALUE_FORMATS.NUMBER,
    type: instrumentType,
  }
}

function processFactorViewMetrics(selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isCompareWeightStrategy) {
  return chain(selectedMetrics)
    .map(metric => {
      if (metric.shouldHide) {
        return null
      }
      const filteredInstrumentColumns = getFilteredTriplicatedColumns(instrumentColumns, metric)
      return filteredInstrumentColumns?.map((instrumentType, index) =>
        transformMetric(metric, instrumentType, index, instrumentColumns, instrumentsWithMetadata, filteredInstrumentColumns, isEndVsStartDateComparison, isCompareWeightStrategy),
      )
    })
    .flatten()
    .compact()
    .value()
}

function processAssetViewMetrics(selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isCompareWeightStrategy) {
  return chain([
    ...instrumentColumns.map(instrumentType => getDefaultDownloadMetrics(instrumentType)),
    ...selectedMetrics.map(metric => {
      if (metric.shouldHide && metric.shouldHide()) {
        return null
      }
      const filteredInstrumentColumns = getFilteredTriplicatedColumns(instrumentColumns, metric)
      return filteredInstrumentColumns?.map((instrumentType, index) => ({
        ...transformMetric(metric, instrumentType, index, instrumentColumns, instrumentsWithMetadata, filteredInstrumentColumns, isEndVsStartDateComparison, isCompareWeightStrategy),
        getAction: (position) => {
          if (!hasAction(metric.action, instrumentType, position, instrumentColumns)) {
            return null
          }
          return () => metric.action(position, instrumentType)
        },
      }))
    }),
  ])
    .flatten()
    .compact()
    .value()
}

function computeMetricsFromDefinition(metricsAndCategories, options) {
  return reduce(metricsAndCategories, (acc, metricOrCategory) => {
    if (metricOrCategory.id) {
      return [...acc, getMetric(metricOrCategory, options)]
    }
    return [...acc, ...getCategory(metricOrCategory, options)]
  }, [])
}

function getMetric(requestedMetric, options) {
  let metricDefinition = ASSET_METRICS_BY_ID[requestedMetric.id] || FACTOR_METRICS_BY_ID[requestedMetric.id]
  if (isFunction(metricDefinition)) {
    metricDefinition = metricDefinition({ ...requestedMetric, ...options })
  }

  return {
    ...requestedMetric,
    ...metricDefinition,
  }
}

function getCategory(item, options) {
  const category = ASSET_METRIC_CATEGORIES[item.category] || FACTOR_METRIC_CATEGORIES[item.category]
  if (isFunction(category)) {
    return category({ ...item, ...options })
  }
  return category
}
